/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import {
  ActionType,
  ConfigurationHelper,
  dispatch,
  IApplicationMenuScreenModel,
  MenuItemsAlignment,
  RouteHelper,
  ScreenType,
  useConfigurationSelector,
  useScreenConfiguration,
} from "@bms/common";
import { push } from "connected-react-router";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useRouteMatch } from "react-router-dom";

import { ApplicationHeaderSearch } from "components/Search/ApplicationHeaderSearch";

import { HamburgerMenu, Menu } from "../Menu";

import { ApplicationMenuItem } from "./ApplicationMenuItem";

import "./ApplicationMenu.scss";

export interface IApplicationMenuProps {
  isMobile?: boolean;
}

export const ApplicationMenu = ({
  isMobile = false,
}: IApplicationMenuProps) => {
  const match = useRouteMatch();
  const { t } = useTranslation();
  const configuration = useConfigurationSelector();
  const screen = useScreenConfiguration<IApplicationMenuScreenModel>(
    ScreenType.ApplicationMenu,
  );

  const itemsAlignment = screen.ItemsAlignment || MenuItemsAlignment.Start;

  const componentsWithoutSearch = useMemo(
    () =>
      screen?.Components
        ? screen?.Components?.filter(
            (component) =>
              component.IsVisible &&
              component.Action?.ScreenTypeCode !== ScreenType.Search,
          )
        : [],
    [screen?.Components],
  );

  const searchComponent = useMemo(
    () =>
      screen?.Components
        ? screen.Components.find(
            (component) =>
              component.IsVisible &&
              component?.Action?.ScreenTypeCode === ScreenType.Search,
          )
        : null,
    [screen?.Components],
  );

  const selectedKeys = useMemo(() => {
    const keys: string[] = [];
    const screenKey = RouteHelper.getScreenRouteKey(match);

    if (screenKey) {
      keys.push(screenKey);

      if (isNaN(Number(screenKey)) && configuration?.Screens?.CUSTOM) {
        const screen = Object.keys(configuration.Screens.CUSTOM)
          .map((k) => configuration?.Screens?.CUSTOM?.[k])
          .find((row) => row?.RouteName === screenKey);

        if (screen) {
          keys.push(`${screen.Id}`);
        } else {
          keys.push(match.url.slice(1));
        }
      }
    }

    return keys;
  }, [match]);

  if (!screen?.Components) {
    return null;
  }

  if (isMobile) {
    return (
      <HamburgerMenu>
        {screen?.Components.map((component) => {
          const screenKey =
            ConfigurationHelper.getApplicationMenuItemScreenKey(component);

          const onClick = () => {
            if (component?.Action?.ActionType) {
              switch (component.Action.ActionType) {
                case ActionType.OpenScreen:
                  if (screenKey) {
                    dispatch(push(`/${screenKey}`));
                  }
                  break;
                case ActionType.OpenUrl:
                  if (component.Action.Url) {
                    window.open(component.Action.Url, "_blank");
                  }
                  break;
              }
            }
          };
          return (
            <div
              className="ApplicationMenu__MobileItems"
              onClick={onClick}
              key={screenKey}
            >
              {t(component?.TitleTranslationKey ?? "", component?.Title ?? "")}
            </div>
          );
        })}
      </HamburgerMenu>
    );
  }

  return (
    <nav className="ApplicationMenu" role="navigation">
      <Menu
        mode="horizontal"
        selectedKeys={selectedKeys}
        triggerSubMenuAction="click"
        data-align={itemsAlignment}
      >
        {componentsWithoutSearch.map((component) => {
          const screenKey =
            ConfigurationHelper.getApplicationMenuItemScreenKey(component);
          return (
            <ApplicationMenuItem
              key={screenKey}
              component={component}
              attribute={{
                "data-selected": selectedKeys.includes(screenKey).toString(),
              }}
            />
          );
        })}
      </Menu>
      {searchComponent && <ApplicationHeaderSearch />}
    </nav>
  );
};
